import { useSnackbar } from '../hooks/useSnackbar';

export interface ErrorHandlerOptions {
  showNotification?: boolean;
  notificationType?: 'error' | 'warning' | 'info';
  logToConsole?: boolean;
  rethrow?: boolean;
}

export class ErrorHandler {
  private static snackbar: ReturnType<typeof useSnackbar> | null = null;

  static setSnackbar(snackbar: ReturnType<typeof useSnackbar>) {
    ErrorHandler.snackbar = snackbar;
  }

  static handleError(
    error: Error | string | unknown,
    options: ErrorHandlerOptions = {}
  ): void {
    const {
      showNotification = true,
      notificationType = 'error',
      logToConsole = true,
      rethrow = false,
    } = options;

    // Convert error to string message
    const errorMessage = this.getErrorMessage(error);

    // Show notification if enabled and snackbar is available
    if (showNotification && this.snackbar) {
      switch (notificationType) {
        case 'error':
          this.snackbar.showError(errorMessage);
          break;
        case 'warning':
          this.snackbar.showWarning(errorMessage);
          break;
        case 'info':
          this.snackbar.showInfo(errorMessage);
          break;
      }
    }

    // Log to console if enabled
    if (logToConsole) {
      // eslint-disable-next-line no-console
      console.error('Error handled:', errorMessage, error);
    }

    // Rethrow if enabled
    if (rethrow) {
      if (error instanceof Error) {
        throw error;
      } else {
        throw new Error(errorMessage);
      }
    }
  }

  static handleAsyncError<T>(
    promise: Promise<T>,
    options: ErrorHandlerOptions = {}
  ): Promise<T> {
    return promise.catch(error => {
      this.handleError(error, options);
      throw error; // Always rethrow for async operations
    });
  }

  private static getErrorMessage(error: Error | string | unknown): string {
    if (error instanceof Error) {
      return error.message;
    }
    if (typeof error === 'string') {
      return error;
    }
    if (error && typeof error === 'object' && 'message' in error) {
      return String(error.message);
    }
    return 'An unexpected error occurred';
  }
}

// Hook to use error handler with snackbar
export const useErrorHandler = () => {
  const snackbar = useSnackbar();

  // Set the snackbar instance for the ErrorHandler class
  ErrorHandler.setSnackbar(snackbar);

  return {
    handleError: (
      error: Error | string | unknown,
      options?: ErrorHandlerOptions
    ) => {
      ErrorHandler.handleError(error, options);
    },
    handleAsyncError: <T>(
      promise: Promise<T>,
      options?: ErrorHandlerOptions
    ) => {
      return ErrorHandler.handleAsyncError(promise, options);
    },
  };
};
